import "./Feedback.scss";

import { Accordion, Badge, Button, ButtonGroup, Card, Col, Row } from "react-bootstrap";
import React, { useEffect, useMemo, useState } from "react";
import { faCommentDots, faGear, faStar, faUserSecret } from "@fortawesome/free-solid-svg-icons";

import AppLoading from "../../commons/AppLoading";
import EmptyState from "../../commons/EmptyState";
import FeedbackAPI from "./FeedbackAPI";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HallAppCenterContainer from "../../commons/containers/HallAppCenterContainer";
import HallAppToast from "../../commons/HallAppToast";
import { useNavigate } from "react-router-dom";

const feedbackAPI = new FeedbackAPI();

function formatHumanDate(input) {
    if (!input) return "";
    const d = new Date(input);
    if (Number.isNaN(d.getTime())) return String(input);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function Stars({ value = 0 }) {
    const v = Math.max(0, Math.min(5, Number(value || 0)));

    let colorClass = "stars-green";
    if (v <= 2) colorClass = "stars-red";
    else if (v === 3) colorClass = "stars-yellow";

    return (
        <span className={`feedback-stars ${colorClass}`} aria-label={`Avaliação ${v} de 5`}>
            {Array.from({ length: 5 }).map((_, i) => (
                <FontAwesomeIcon key={i} icon={faStar} className={i < v ? "star active" : "star"} />
            ))}
        </span>
    );
}

const FILTERS = {
    ALL: { label: "Todos" },
    BAD: { label: "Ruim" },          // 1-2
    OK: { label: "Mais ou menos" },  // 3
    GOOD: { label: "Bom" },          // 4-5
};

const Feedback = () => {
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);

    const [feedbacks, setFeedbacks] = useState([]);
    const [totalResultRows, setTotalResultRows] = useState(0);

    const [isShowNotFoundPage, setShowNotfoundPage] = useState(false);

    const [summary, setSummary] = useState({
        total: 0,
        averageRating: 0
    });

    const [filter, setFilter] = useState({
        page: 0,
        limit: 20,
        ratingFilter: "ALL", // ALL | BAD | OK | GOOD
    });

    useEffect(() => {
        if (filter.page === 0) {
            setFeedbacks([]);
            setTotalResultRows(0);
        }
        getFeedbacks();
        getFeedbackSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.page, filter.limit, filter.ratingFilter]);

    const getFeedbackSummary = async () => {
        try {
            const resp = await feedbackAPI.getFeedbackSummary(filter.ratingFilter);
            const data = resp?.data;

            if (data) {
                setSummary({
                    total: data.total || 0,
                    averageRating: data.averageRating || 0
                });
            }
        } catch {
            // summary é informativo → não quebra a tela
            setSummary({ total: 0, averageRating: 0 });
        }
    };

    const getFeedbacks = async () => {
        try {
            setIsLoading(true);

            const resp = await feedbackAPI.getFeedbacks(filter.page, filter.limit, filter.ratingFilter);

            // suporta paginação padrão: { content, totalElements }
            // ou seu padrão: { content, totalResultRows }
            const data = resp?.data || {};
            const content = Array.isArray(data) ? data : (data.content || []);
            const total =
                typeof data.totalResultRows === "number"
                    ? data.totalResultRows
                    : (typeof data.totalElements === "number" ? data.totalElements : content.length);

            setFeedbacks((prev) => [...prev, ...content]);
            setTotalResultRows(total);

            const noItems = filter.page === 0 && (!content || content.length === 0);
            setShowNotfoundPage(noItems);
        } catch {
            if (filter.page === 0) {
                setFeedbacks([]);
                setTotalResultRows(0);
                setShowNotfoundPage(true);
            }
            HallAppToast.genericError();
        } finally {
            setIsLoading(false);
        }
    };

    const items = useMemo(() => {
        // mantém ordenação por data (caso o back não ordene)
        return [...feedbacks].sort((a, b) => {
            const da = a?.serviceDate ? new Date(a.serviceDate).getTime() : 0;
            const db = b?.serviceDate ? new Date(b.serviceDate).getTime() : 0;
            return db - da;
        });
    }, [feedbacks]);

    const hasMore = feedbacks.length < totalResultRows;

    const loadMore = () => {
        if (!hasMore) return;
        setFilter((prev) => ({ ...prev, page: prev.page + 1 }));
    };

    const changeRatingFilter = (ratingFilter) => {
        setFilter((prev) => ({
            ...prev,
            ratingFilter,
            page: 0, // ✅ reset paginação ao trocar filtro
        }));
    };

    return (
        <>
            {isLoading && filter.page === 0 ? (
                <HallAppCenterContainer>
                    <AppLoading />
                </HallAppCenterContainer>
            ) : (
                <>

                    <div className="container">
                        <div className="feedback-header">
                            <div>
                                <h2>Feedbacks</h2>

                                <div className="feedback-summary">
                                    <div className="feedback-summary-stars">
                                        <Stars value={summary.averageRating} />
                                        <span className="feedback-summary-avg">
                                            {summary.averageRating.toFixed(1)}
                                        </span>
                                        <span className="feedback-summary-label">
                                            nota média da sua empresa
                                        </span>
                                    </div>

                                    <span className="feedback-subtitle">
                                        {summary.total} {summary.total === 1 ? "avaliação" : "avaliações"}
                                    </span>
                                </div>

                            </div>


                            {/* ✅ filtro topo */}
                            <div className={`feedback-filter-group ${filter.ratingFilter.toLowerCase()}`}>
                                <button
                                    className={`feedback-filter-btn all ${filter.ratingFilter === "ALL" ? "active" : ""}`}
                                    onClick={() => changeRatingFilter("ALL")}
                                >
                                    Todos
                                </button>

                                <button
                                    className={`feedback-filter-btn bad ${filter.ratingFilter === "BAD" ? "active" : ""}`}
                                    onClick={() => changeRatingFilter("BAD")}
                                >
                                    Ruim
                                </button>

                                <button
                                    className={`feedback-filter-btn ok ${filter.ratingFilter === "OK" ? "active" : ""}`}
                                    onClick={() => changeRatingFilter("OK")}
                                >
                                    Mais ou menos
                                </button>

                                <button
                                    className={`feedback-filter-btn good ${filter.ratingFilter === "GOOD" ? "active" : ""}`}
                                    onClick={() => changeRatingFilter("GOOD")}
                                >
                                    Bom
                                </button>
                            </div>

                        </div>
                    </div>
                    {isShowNotFoundPage ? (
                        <div style={{ marginTop: "120px" }}>
                            <EmptyState
                                title="Nenhum feedback ainda"
                                description={
                                    <div style={{ textAlign: "center" }}>
                                        <p style={{ marginBottom: "10px" }}>
                                            Para começar a receber feedbacks dos seus clientes é necessário:
                                        </p>

                                        <p style={{ marginBottom: "6px" }}>
                                            Adicionar o <strong>WhatsApp da empresa</strong>
                                        </p>

                                        <p style={{ marginBottom: "12px" }}>
                                            <FontAwesomeIcon icon={faGear} /> Habilitar o{" "}
                                            <strong>envio de notificações de feedback</strong>
                                        </p>

                                        <Button variant="success" onClick={() => navigate("/whats-config")}>
                                            Configurar WhatsApp
                                        </Button>
                                    </div>
                                }
                                icon={<FontAwesomeIcon icon={faCommentDots} />}
                            />
                        </div>
                    ) : (
                        <div className="feedback-page container">


                            <Row style={{ margin: 0, paddingTop: "20px", paddingBottom: "120px" }}>
                                {items.map((fb) => (
                                    <Col key={fb.id} xs={12} sm={12} md={6} lg={4} style={{ marginBottom: "12px" }}>
                                        <Card className="feedback-card">
                                            <Card.Body>
                                                <div className="feedback-top">
                                                    <div className="feedback-title">
                                                        <div className="feedback-name">
                                                            {fb.anonymous ? (
                                                                <>
                                                                    <FontAwesomeIcon icon={faUserSecret} /> <span>Anônimo</span>
                                                                </>
                                                            ) : (
                                                                <span>{fb.customerName || "Cliente"}</span>
                                                            )}
                                                        </div>
                                                        <div className="feedback-date">{formatHumanDate(fb.serviceDate)}</div>
                                                    </div>

                                                    <div className="feedback-rating">
                                                        <Stars value={fb.rating} />
                                                        <Badge bg="light" text="dark" className="rating-badge">
                                                            {Number(fb.rating || 0)}/5
                                                        </Badge>
                                                    </div>
                                                </div>

                                                {!!fb.comment && <div className="feedback-comment">“{fb.comment}”</div>}

                                                {fb.anonymous ? (
                                                    <div className="feedback-anon-note">
                                                        Por ser anônimo, não exibimos nome e detalhes dos serviços.
                                                    </div>
                                                ) : (
                                                    Array.isArray(fb.services) &&
                                                    fb.services.length > 0 && (
                                                        <Accordion className="feedback-accordion">
                                                            <Accordion.Item eventKey="0">
                                                                <Accordion.Header>Serviços ({fb.services.length})</Accordion.Header>
                                                                <Accordion.Body>
                                                                    <ul className="feedback-services">
                                                                        {fb.services.map((s, idx) => (
                                                                            <li key={idx} className="feedback-service-item">
                                                                                <div className="service-name">{s.serviceName}</div>
                                                                                <div className="employee-name">{s.employeeName}</div>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </Accordion.Body>
                                                            </Accordion.Item>
                                                        </Accordion>
                                                    )
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                            <div style={{ height: "calc(50px + 72px)" }}></div>
                            <div className="feedback-loadmore">
                                {hasMore ? (
                                    <Button variant="outline-secondary" onClick={loadMore} disabled={isLoading}>
                                        {isLoading ? "Carregando..." : "Carregar mais"}
                                    </Button>
                                ) : (
                                    <div className="feedback-end">Você já viu tudo</div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default Feedback;
